import { useTranslation } from 'react-i18next';

import type {
  AnalyticsDestinationStatus,
  ForwardStatus,
  ForwardStatusStatus,
} from '@nucleus-commerce/api-client/admin';
import { Badge } from '@nucleus-commerce/ui/components/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@nucleus-commerce/ui/components/tooltip';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useForwardReasonText } from '../hooks/use-forward-reason-text';

const SUCCESS = 'bg-success/15 text-success border-transparent dark:bg-success/20';
const WARNING = 'bg-warning/20 text-warning-foreground border-transparent dark:bg-warning/25';
const NEUTRAL = 'border-border text-muted-foreground';
const DANGER = 'bg-destructive/10 text-destructive border-transparent';

const FORWARD: Record<ForwardStatusStatus, string> = {
  sent: SUCCESS,
  failed: DANGER,
  skipped: NEUTRAL,
};

export function DestinationStatusBadge({
  status,
  className,
}: {
  status: AnalyticsDestinationStatus;
  className?: string;
}) {
  const { t } = useTranslation('analytics');
  return (
    <Badge variant="outline" className={cn(status === 'active' ? SUCCESS : WARNING, className)}>
      {t(`status.${status}`)}
    </Badge>
  );
}

/** One provider's outcome for an event; the reason sits in a tooltip and the accessible name. */
export function ForwardBadge({
  provider,
  forward,
}: {
  provider: string;
  forward: ForwardStatus | undefined;
}) {
  const { t } = useTranslation('analytics');
  const reasonText = useForwardReasonText();
  if (!forward) {
    return <span className="text-muted-foreground text-xs">{t('forward.notForwarded')}</span>;
  }
  const status = t(`forward.status.${forward.status}`);
  const reason = reasonText(forward);
  const detail = [
    forward.httpStatus !== null ? t('test.httpStatus', { status: forward.httpStatus }) : null,
    reason,
  ]
    .filter(Boolean)
    .join(' · ');
  const badge = (
    <Badge
      variant="outline"
      tabIndex={detail ? 0 : undefined}
      aria-label={t('forward.detail', { provider, status })}
      aria-description={detail || undefined}
      className={cn(FORWARD[forward.status])}
    >
      {status}
    </Badge>
  );
  return (
    <span className="flex min-w-0 flex-col items-start gap-0.5">
      {detail ? (
        <Tooltip>
          <TooltipTrigger asChild>{badge}</TooltipTrigger>
          <TooltipContent className="max-w-sm break-words">{detail}</TooltipContent>
        </Tooltip>
      ) : (
        badge
      )}
      {reason && forward.status === 'skipped' ? (
        <span className="text-muted-foreground max-w-40 truncate text-xs">{reason}</span>
      ) : null}
    </span>
  );
}
